"use client";
import { FC, HTMLAttributes, useMemo, useState } from "react";

import TButton from "@/components/atoms/buttons/TButton";

import BlogCard, { IBlogCard } from "@/components/cards/BlogCard";
import { useInfiniteQuery } from "@tanstack/react-query";
import { blogInfiniteQuery } from "@/api/blog";
import { isEmpty } from "lodash";
import { getImageUrl } from "@/utils/media.helper";

// -------------------------------------------------------------------------------------------------------

export type ILatestBlogsSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  blogs?: (IBlogCard & { link: string })[];
  showLoadMoreButton?: boolean;
  initialBlogCount?: number;
};

const LatestBlogsSection: FC<ILatestBlogsSectionProps> = (props) => {
  const {
    heading,
    blogs,
    showLoadMoreButton = false,
    initialBlogCount,
    className,
    ...other
  } = props;

  const [showBlogs, setShowBlogs] = useState<number>(initialBlogCount ?? 9);

  const { data, fetchNextPage, hasNextPage, isError, isLoading, isFetching } =
    useInfiniteQuery({
      ...blogInfiniteQuery.all({
        sort: "publish_date:DESC",
        populate: "deep",
        pagination: {
          pageSize: 13,
        },
        filters: {
          category: {
            $eq: "technical",
          },
        },
      }),
      select: (data) => {
        const [firstPage, ...restPages] = data.pages;

        return {
          ...data,
          pages: [
            { ...firstPage, data: firstPage.data.slice(4) },
            ...restPages,
          ],
        };
      },
    });

  const infiniteBlogs = useMemo(() => {
    return data?.pages.flatMap((page) => page.data.flat());
  }, [data?.pages]);

  if (isError || !infiniteBlogs || isEmpty(infiniteBlogs)) {
    return null;
  }

  const loadMore = () => {
    setShowBlogs((pre) =>
      initialBlogCount ? pre + initialBlogCount : pre + 9,
    );
    hasNextPage && fetchNextPage();
  };

  return (
    <section
      className="bg-white py-5 lg:py-8 xl:py-[50px]"
      id="latest-blogs"
      {...other}
    >
      <div className="container">
        {heading && (
          <h2 className="mb-12 border-b-2 border-yellow-400 pb-5 text-h5 font-medium text-black-900 md:mb-14 lg:mb-20 lg:text-h3 xl:mb-[100px] xl:text-h2">
            {heading}
          </h2>
        )}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {infiniteBlogs.slice(0, showBlogs).map(({ id, attributes }) => (
            <BlogCard
              figureWrapperClass="md:h-[184px] lg:h-[153px] xl:h-[213px] w-full aspect-[5/3]"
              key={id}
              link={`${attributes.category === "technical" ? "/technical-blogs" : "/leadership-insights"}/${attributes.slug}`}
              publishDate={attributes.publish_date}
              readingTime={attributes.reading_time}
              image={{
                src: getImageUrl(attributes.image),
                alt: attributes.image?.name ?? "",
              }}
              heading={attributes.title}
              author={{
                image: getImageUrl(attributes.author?.data?.attributes.image),
                name: attributes.author?.data?.attributes.name,
              }}
              variant="white"
            />
          ))}
        </div>

        {(showBlogs < infiniteBlogs.length || hasNextPage) && (
          <div className="mt-8 text-center md:mt-10 xl:mt-[60px]">
            <TButton
              id="load-more-technical-blogs-button"
              variant="primary"
              onClick={() => loadMore()}
              disabled={isFetching || isLoading}
            >
              Load More
            </TButton>
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestBlogsSection;
